import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { v4 as uuidv4 } from 'uuid';
import { useStore } from '@/store';
import { UPDATE_PREFERENCES, CREATE_SESSION, UPDATE_BOOKING_SESSION, CONFIRM_BOOKING } from '@/lib/graphql/mutations';
import { SEARCH_FLIGHTS } from '@/lib/graphql/queries';
import { BirthdatePicker } from '@/components/shared/BirthdatePicker';
import { AIRPORTS } from '@/lib/airports';
import { clsx } from 'clsx';

let _client: { graphql: Function } | null = null;
const getClient = () => { if (!_client) _client = generateClient(); return _client; };

const SEAT_OPTIONS   = ['Window', 'Aisle', 'Middle', 'No preference'];
const CABIN_OPTIONS  = [{ v: 'ECONOMY', l: 'Economy' }, { v: 'PREMIUM_ECONOMY', l: 'Premium Economy' }, { v: 'BUSINESS', l: 'Business' }, { v: 'FIRST', l: 'First Class' }];
const MEAL_OPTIONS   = ['None', 'Vegetarian', 'Vegan', 'Halal', 'Kosher', 'Gluten-free', 'Nut allergy', 'Dairy-free', 'Low sodium'];
const TRAVEL_OPTIONS = ['Business', 'Leisure', 'No preference'];
const LANG_OPTIONS   = ['English', 'French', 'Spanish', 'Arabic', 'German', 'Chinese', 'Portuguese', 'Japanese', 'Korean'];

const inputCls = 'w-full border border-white/10 rounded-xl px-3 py-2.5 text-sm bg-white/5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-brand-400 transition-colors';
const selectCls = `${inputCls} appearance-none [color-scheme:dark]`;

function StepDot({ n, current }: { n: number; current: number }) {
  const done = n < current;
  const active = n === current;
  return (
    <div className="flex items-center gap-0">
      <div className={clsx(
        'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors',
        active ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/40 scale-110'
        : done  ? 'bg-brand-500/30 text-brand-300'
        : 'bg-white/10 text-white/30',
      )}>
        {done ? '✓' : n}
      </div>
    </div>
  );
}

export function OnboardingWizard() {
  const navigate = useNavigate();
  const {
    setAuth, userName,
    prefSeatLocation, prefCabinClass, prefMealType, prefHomeAirport, prefTravelType, prefLanguage,
    birthDate: storedBirthDate, addressLine1: storedLine1, addressCity: storedCity,
    addressState: storedState, addressZip: storedZip, addressCountry: storedCountry,
    cognitoSub,
  } = useStore();

  const [step, setStep]             = useState(1);
  const [submitting, setSubmitting]  = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [error, setError]            = useState<string | null>(null);
  const [autoBookingCode, setAutoBookingCode] = useState<string | null>(null);

  // Pre-fill from store so existing data is preserved if wizard was reset
  const [prefs, setPrefs] = useState({
    prefSeatLocation: prefSeatLocation ?? '',
    prefCabinClass:   prefCabinClass   ?? '',
    prefMealType:     prefMealType     ?? '',
    prefHomeAirport:  prefHomeAirport  ?? '',
    prefTravelType:   prefTravelType   ?? '',
    prefLanguage:     prefLanguage     ?? '',
  });
  const [personal, setPersonal] = useState({
    birthDate:      storedBirthDate ?? '',
    addressLine1:   storedLine1     ?? '',
    addressCity:    storedCity      ?? '',
    addressState:   storedState     ?? '',
    addressZip:     storedZip       ?? '',
    addressCountry: storedCountry   ?? '',
  });

  async function savePrefsAndContinue() {
    setSavingPrefs(true);
    try {
      await (getClient().graphql({
        query: UPDATE_PREFERENCES,
        authMode: 'userPool',
        variables: { input: {
          prefSeatLocation: prefs.prefSeatLocation || undefined,
          prefCabinClass:   prefs.prefCabinClass   || undefined,
          prefMealType:     prefs.prefMealType     || undefined,
          prefHomeAirport:  prefs.prefHomeAirport  || undefined,
          prefTravelType:   prefs.prefTravelType   || undefined,
          prefLanguage:     prefs.prefLanguage     || undefined,
        }},
      }) as Promise<unknown>);
      setAuth({
        prefSeatLocation: prefs.prefSeatLocation || null,
        prefCabinClass:   prefs.prefCabinClass   || null,
        prefMealType:     prefs.prefMealType     || null,
        prefHomeAirport:  prefs.prefHomeAirport  || null,
        prefTravelType:   prefs.prefTravelType   || null,
        prefLanguage:     prefs.prefLanguage     || null,
      });
    } catch (err) {
      console.warn('[wizard] savePrefs failed (non-fatal):', err);
    } finally {
      setSavingPrefs(false);
    }
    setStep(3);
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      await (getClient().graphql({
        query: UPDATE_PREFERENCES,
        authMode: 'userPool',
        variables: {
          input: {
            prefSeatLocation: prefs.prefSeatLocation  || undefined,
            prefCabinClass:   prefs.prefCabinClass    || undefined,
            prefMealType:     prefs.prefMealType      || undefined,
            prefHomeAirport:  prefs.prefHomeAirport   || undefined,
            prefTravelType:   prefs.prefTravelType    || undefined,
            prefLanguage:     prefs.prefLanguage      || undefined,
            birthDate:        personal.birthDate      || undefined,
            addressLine1:     personal.addressLine1   || undefined,
            addressCity:      personal.addressCity    || undefined,
            addressState:     personal.addressState   || undefined,
            addressZip:       personal.addressZip     || undefined,
            addressCountry:   personal.addressCountry || undefined,
            wizardCompleted:  true,
          },
        },
      }) as Promise<unknown>);

      setAuth({
        prefSeatLocation: prefs.prefSeatLocation  || null,
        prefCabinClass:   prefs.prefCabinClass    || null,
        prefMealType:     prefs.prefMealType      || null,
        prefHomeAirport:  prefs.prefHomeAirport   || null,
        prefTravelType:   prefs.prefTravelType    || null,
        prefLanguage:     prefs.prefLanguage      || null,
        birthDate:        personal.birthDate      || null,
        addressLine1:     personal.addressLine1   || null,
        addressCity:      personal.addressCity    || null,
        addressState:     personal.addressState   || null,
        addressZip:       personal.addressZip     || null,
        addressCountry:   personal.addressCountry || null,
        // wizardCompleted set on final button click so the wizard stays visible on step 4
      });

      setStep(4);

      // Auto-create a demo booking (non-blocking — failure doesn't affect wizard)
      createDemoBooking(prefs.prefHomeAirport || 'JFK').catch(e =>
        console.warn('[wizard] auto-booking failed (non-fatal):', e)
      );

    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('[wizard] submit failed:', err);
    } finally {
      setSubmitting(false);
    }
  }

  async function createDemoBooking(origin: string) {
    // Departure 30 days from today
    const departureDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString().split('T')[0];
    const destination = 'NRT'; // Tokyo — always a compelling demo destination

    // 1. Search flights (synthetic fallback ensures results always exist)
    const flightRes = await (getClient().graphql({
      query: SEARCH_FLIGHTS,
      variables: { input: { origin, destination, departureDate, cabinClass: 'ECONOMY', passengerCount: 1 } },
    }) as Promise<{ data: { searchFlights: { flightId: string; flightNumber: string; airline: string; origin: string; destination: string; cabinClass: string; departureTime: string; arrivalTime: string; durationMinutes: number; price: number; currency: string; seatsAvailable: number }[] } }>);

    const flight = flightRes.data.searchFlights[0];
    if (!flight) return;

    // 2. Create a new session for this booking
    const sessionRes = await (getClient().graphql({
      query: CREATE_SESSION,
    }) as Promise<{ data: { createSession: { sessionId: string } } }>);
    const sessionId = sessionRes.data.createSession.sessionId;

    // 3. Set booking state on the session
    const nameParts  = (userName ?? '').split(' ');
    const passenger  = { passengerId: uuidv4(), firstName: nameParts[0] ?? 'Traveler', lastName: nameParts.slice(1).join(' ') || 'Demo', dateOfBirth: '', passportNumber: '', nationality: '' };

    await (getClient().graphql({
      query: UPDATE_BOOKING_SESSION,
      variables: {
        sessionId,
        patch: {
          status:         'PAYMENT',
          currentStep:    5,
          userId:         cognitoSub ?? undefined,
          selectedFlight: JSON.stringify(flight),
          selectedSeats:  ['15A'],
          passengers:     [passenger],
        },
      },
    }) as Promise<unknown>);

    // 4. Confirm — generates BOOKING# record → DDB stream → CP sync
    const confirmRes = await (getClient().graphql({
      query: CONFIRM_BOOKING,
      variables: { sessionId },
    }) as Promise<{ data: { confirmBooking: { confirmationCode: string } } }>);

    const code = confirmRes.data.confirmBooking.confirmationCode;
    setAutoBookingCode(code);
    console.log(`[wizard] Demo booking created: ${origin}→${destination} code=${code}`);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(5,10,10,0.92)', backdropFilter: 'blur(8px)' }}>

      <div className="w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl"
        style={{ background: 'linear-gradient(170deg, #0D1F22 0%, #050A0A 100%)' }}>

        {/* Progress bar */}
        {step < 4 && (
          <div className="px-8 pt-7 pb-2 flex items-center gap-2">
            {[1, 2, 3].map((n, i) => (
              <React.Fragment key={n}>
                <StepDot n={n} current={step} />
                {i < 2 && (
                  <div className="flex-1 h-px" style={{
                    background: n < step ? '#4A8B8C' : 'rgba(255,255,255,0.1)'
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* ── Step 1: Welcome ── */}
        {step === 1 && (
          <div>
            <div className="px-8 pt-6 pb-8">
              {/* Logo */}
              <img
                src="https://cdn.prod.website-files.com/69148a80ebab6c41c3621a67/693ba81b001c674edfb758d7_LOGO-PRONETX-Horiz-Black-2-tone-001%202.png"
                alt="Pronetx Airlines"
                className="h-6 w-auto object-contain invert brightness-90 mb-6"
              />

              <div className="inline-flex items-center gap-2 bg-brand-500/15 border border-brand-500/30 rounded-full px-3 py-1 mb-5">
                <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse" />
                <span className="text-brand-400 text-[11px] font-semibold tracking-widest uppercase">Amazon Connect Demo</span>
              </div>

              <h1 className="text-2xl font-bold text-white mb-3 leading-snug">
                Welcome{userName ? `, ${userName.split(' ')[0]}` : ''}.<br />
                Let's set up your<br />demo profile.
              </h1>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Pronetx Airlines is a fictitious airline built to showcase Amazon Connect's
                Customer Experience capabilities. All information you provide is for
                demo purposes only.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  { icon: '⚡', title: 'Real-time AI Agents', desc: 'Watch Amazon Connect agents interact with your live booking session' },
                  { icon: '🧠', title: 'Customer Profiles', desc: 'Your preferences sync instantly to Amazon Connect Customer Profiles' },
                  { icon: '✈', title: 'Personalised Experience', desc: 'Agents see your full history, preferences and booking state' },
                ].map(f => (
                  <div key={f.title} className="flex items-start gap-3 p-3 rounded-xl bg-white/5">
                    <span className="text-lg shrink-0">{f.icon}</span>
                    <div>
                      <p className="text-white text-sm font-semibold">{f.title}</p>
                      <p className="text-gray-500 text-xs mt-0.5">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={() => setStep(2)}
                className="w-full bg-brand-500 hover:bg-brand-400 text-white font-bold py-3.5 rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
                Set Up My Profile
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
                  className="w-4 h-4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Travel Preferences ── */}
        {step === 2 && (
          <div className="px-8 pt-4 pb-8">
            <p className="text-[11px] font-semibold text-brand-400 uppercase tracking-widest mb-2">Step 1 of 2</p>
            <h2 className="text-xl font-bold text-white mb-1">How do you like to travel?</h2>
            <p className="text-gray-500 text-sm mb-6">
              These preferences are synced to your Amazon Connect Customer Profile so our AI agents can personalise your experience.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">Seat</p>
                <div className="relative">
                  <select value={prefs.prefSeatLocation} onChange={e => setPrefs(p => ({ ...p, prefSeatLocation: e.target.value }))} className={selectCls}>
                    <option value="">Any</option>
                    {SEAT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">Cabin</p>
                <select value={prefs.prefCabinClass} onChange={e => setPrefs(p => ({ ...p, prefCabinClass: e.target.value }))} className={selectCls}>
                  <option value="">Any</option>
                  {CABIN_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">Dietary</p>
                <select value={prefs.prefMealType} onChange={e => setPrefs(p => ({ ...p, prefMealType: e.target.value }))} className={selectCls}>
                  {MEAL_OPTIONS.map(o => <option key={o} value={o === 'None' ? '' : o}>{o}</option>)}
                </select>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">Home Airport</p>
                <select value={prefs.prefHomeAirport} onChange={e => setPrefs(p => ({ ...p, prefHomeAirport: e.target.value }))} className={selectCls}>
                  <option value="">Select</option>
                  {AIRPORTS.map(a => <option key={a.code} value={a.code}>{a.city} ({a.code})</option>)}

                </select>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">Travel Type</p>
                <select value={prefs.prefTravelType} onChange={e => setPrefs(p => ({ ...p, prefTravelType: e.target.value }))} className={selectCls}>
                  <option value="">Any</option>
                  {TRAVEL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">Language</p>
                <select value={prefs.prefLanguage} onChange={e => setPrefs(p => ({ ...p, prefLanguage: e.target.value }))} className={selectCls}>
                  <option value="">Any</option>
                  {LANG_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="px-5 py-3 rounded-xl text-sm text-gray-500 hover:text-gray-300 transition-colors">← Back</button>
              <button onClick={savePrefsAndContinue} disabled={savingPrefs}
                className="flex-1 bg-brand-500 hover:bg-brand-400 text-white font-bold py-3 rounded-xl text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {savingPrefs ? (
                  <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Saving…</>
                ) : (
                  <>Continue <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg></>
                )}
              </button>
            </div>
            <button onClick={() => setStep(3)} className="w-full mt-2 text-center text-xs text-gray-600 hover:text-gray-400 transition-colors py-1">
              Skip for now
            </button>
          </div>
        )}

        {/* ── Step 3: Personal Info ── */}
        {step === 3 && (
          <div className="px-8 pt-4 pb-8">
            <p className="text-[11px] font-semibold text-brand-400 uppercase tracking-widest mb-2">Step 2 of 2</p>
            <h2 className="text-xl font-bold text-white mb-1">A bit about you</h2>
            <p className="text-gray-500 text-sm mb-6">
              Used to demonstrate how AI agents can access and use customer data in real time for personalisation.
            </p>

            <div className="space-y-3 mb-6">
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">Date of Birth</p>
                <BirthdatePicker
                  value={personal.birthDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={d => setPersonal(p => ({ ...p, birthDate: d }))}
                  dark
                />
              </div>

              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">Street Address</p>
                <input value={personal.addressLine1} onChange={e => setPersonal(p => ({ ...p, addressLine1: e.target.value }))}
                  placeholder="123 Main Street" className={inputCls} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">City</p>
                  <input value={personal.addressCity} onChange={e => setPersonal(p => ({ ...p, addressCity: e.target.value }))}
                    placeholder="New York" className={inputCls} />
                </div>
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">State</p>
                  <input value={personal.addressState} onChange={e => setPersonal(p => ({ ...p, addressState: e.target.value }))}
                    placeholder="NY" className={inputCls} />
                </div>
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">Zip</p>
                  <input value={personal.addressZip} onChange={e => setPersonal(p => ({ ...p, addressZip: e.target.value }))}
                    placeholder="10001" className={inputCls} />
                </div>
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">Country</p>
                  <input value={personal.addressCountry} onChange={e => setPersonal(p => ({ ...p, addressCountry: e.target.value }))}
                    placeholder="US" className={inputCls} />
                </div>
              </div>
            </div>

            {error && <p className="text-sm text-red-400 bg-red-900/20 px-3 py-2 rounded-lg mb-4">{error}</p>}

            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="px-5 py-3 rounded-xl text-sm text-gray-500 hover:text-gray-300 transition-colors">← Back</button>
              <button onClick={handleSubmit} disabled={submitting}
                className="flex-1 bg-brand-500 hover:bg-brand-400 text-white font-bold py-3 rounded-xl text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting ? (
                  <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Saving…</>
                ) : (
                  <>Save & Complete</>
                )}
              </button>
            </div>
            <button onClick={handleSubmit} disabled={submitting} className="w-full mt-2 text-center text-xs text-gray-600 hover:text-gray-400 transition-colors py-1 disabled:opacity-40">
              Skip personal info
            </button>
          </div>
        )}

        {/* ── Step 4: All set ── */}
        {step === 4 && (
          <div className="px-8 pt-8 pb-10 text-center">
            <div className="w-16 h-16 rounded-full bg-brand-500/20 border border-brand-500/40 flex items-center justify-center mx-auto mb-5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
                className="w-8 h-8 text-brand-400" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-white mb-2">You're all set!</h2>
            <p className="text-gray-400 text-sm mb-8 max-w-sm mx-auto">
              Your profile and preferences are saved. Now dial in to experience our Amazon Connect AI agent interacting with your data in real time.
            </p>

            {/* Phone number card */}
            <div className="rounded-2xl p-5 mb-8 text-left" style={{ background: 'linear-gradient(135deg, #0D2428, #0D1F22)', border: '1px solid rgba(74,139,140,0.3)' }}>
              <p className="text-[11px] font-bold text-brand-400 uppercase tracking-widest mb-2">Call the Demo</p>
              <p className="text-3xl font-bold font-mono text-white tracking-wider mb-2">(877) 828-1449</p>
              {autoBookingCode && (
                <div className="mt-2 mb-2 flex items-center gap-2">
                  <span className="text-[10px] text-gray-500 uppercase tracking-widest">Demo booking</span>
                  <span className="font-mono font-bold text-brand-400 text-sm tracking-widest">{autoBookingCode}</span>
                </div>
              )}
              <p className="text-gray-500 text-xs">
                Ask the agent to retrieve your booking, update your preferences, or assist with your flight — all powered by Amazon Connect.
              </p>
            </div>

            <button
              onClick={() => { setAuth({ wizardCompleted: true }); navigate('/contact'); }}
              className="w-full bg-brand-500 hover:bg-brand-400 text-white font-bold py-3.5 rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
              Try the Demo — Call Us
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
                className="w-4 h-4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
